import React, { useEffect, useRef, useState } from 'react';
import { VolumeX } from 'lucide-react';
import { useWholeFileMedia } from '../../hooks/useWholeFileMedia';

/**
 * SHOWCASE REEL — the landing hero's proof-of-output video: a ~57s vertical piece our own studio produced
 * (spectacle → scale → a diverse cast that stays consistent → the brand out), with native scene audio, an
 * original score and narration.
 *
 * Autoplays MUTED + looping (the only way browsers allow autoplay), with an explicit unmute control so a visitor
 * can actually hear it — the audio is half the proof. Presentational only (SteveJobs layering): no store/service
 * imports. Fails soft: if the file can't load the whole block hides itself and the collage below still carries
 * the page. Honours prefers-reduced-motion by not autoplaying.
 */
/** The reels we can show. Keyed so a caller picks a story, not a file path. */
const REELS = {
  intro: {
    src: '/landing/intro.mp4',
    poster: '/landing/intro-poster.jpg',
    captions: '/landing/intro.vtt',
    label: 'A short film made with Wisetree AI',
  },
  intro2: {
    src: '/landing/intro2.mp4',
    poster: '/landing/intro2-poster.jpg',
    captions: '/landing/intro2.vtt',
    label: 'The Last Human Story — a short film made with Wisetree AI',
  },
} as const;

export type ReelKey = keyof typeof REELS;

/**
 * `MediaError.MEDIA_ERR_ABORTED`, by value. The `MediaError` global does not exist in jsdom and is not
 * guaranteed on every runtime we render in, so the constant is named here rather than reached for.
 */
const MEDIA_ERR_ABORTED = 1;

const prefersReducedMotion = (): boolean =>
  typeof window !== 'undefined' && !!window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

const ShowcaseReel: React.FC<{ className?: string; reel?: ReelKey }> = ({ className = '', reel = 'intro' }) => {
  const videoRef = useRef<HTMLVideoElement>(null);
  const [muted, setMuted] = useState(true);
  const [, setPlaying] = useState(!prefersReducedMotion());
  const [failed, setFailed] = useState(false);
  /** Even the poster is gone — there is nothing honest left to show, so the section removes itself. */
  const [hidden, setHidden] = useState(false);

  const { src: SRC, poster: POSTER, captions: CAPTIONS, label } = REELS[reel];

  /**
   * The recovery for a host that will not serve this file in range-addressable pieces — see
   * `useWholeFileMedia`. Streaming is still what runs first; this only exists for when streaming cannot work.
   */
  const { blobSrc, recover } = useWholeFileMedia(SRC);

  // A blob arrives after the element has already given up on the streamed URL, so it has to be told to try
  // again. Without an explicit load() the new src sits there unread and the reel stays frozen on frame one.
  useEffect(() => {
    const v = videoRef.current;
    if (!blobSrc || !v) return;
    v.load();
    if (!prefersReducedMotion()) void v.play().catch(() => undefined);
  }, [blobSrc]);

  /**
   * A genuine failure hides the PLAYER, not the section: the poster is a real frame from the film and carries
   * the same proof. Returning null used to leave a hole in the hero where the only piece of evidence was.
   */
  if (hidden) return null;

  const toggleSound = () => {
    const v = videoRef.current;
    if (!v) return;
    v.muted = false;
    // A muted autoplay may still be paused on some browsers; unmuting should also start it.
    if (v.paused) void v.play().catch(() => undefined);
    setMuted(false);
  };

  return (
    <section className={className} aria-label={label}>
      {/*
        A PHONE, not a video box (owner 2026-08-22). The reel is a vertical micro-drama, and a viewer reads
        "this is what my audience sees on their phone" instantly from the shape — a plain rounded rectangle
        made it look like a website video. Drawn in CSS rather than a PNG overlay: it scales, it themes, and
        it costs no asset. The device shell is aria-hidden decoration; the <video> inside keeps its own label.
      */}
      <div className="relative mx-auto w-full max-w-[300px] rounded-[2.25rem] border-[3px] border-secondary-800 bg-secondary-900
                      p-[7px] shadow-[0_12px_40px_-12px_rgba(0,0,0,.55)] ring-1 ring-secondary-700/60 sm:max-w-[340px]
                      dark:border-secondary-700">
        {/* The notch/island. Sits ON the screen edge like a modern iPhone, above the frame's own overflow clip. */}
        <span
          aria-hidden
          className="pointer-events-none absolute left-1/2 top-[13px] z-20 h-[18px] w-[86px] -translate-x-1/2
                     rounded-full bg-secondary-950"
        />
        {/* Side buttons — three on the left, one on the right, in the real proportions. Pure decoration. */}
        <span aria-hidden className="pointer-events-none absolute -left-[5px] top-[84px] h-7 w-[3px] rounded-l bg-secondary-700 dark:bg-secondary-600" />
        <span aria-hidden className="pointer-events-none absolute -left-[5px] top-[124px] h-11 w-[3px] rounded-l bg-secondary-700 dark:bg-secondary-600" />
        <span aria-hidden className="pointer-events-none absolute -left-[5px] top-[180px] h-11 w-[3px] rounded-l bg-secondary-700 dark:bg-secondary-600" />
        <span aria-hidden className="pointer-events-none absolute -right-[5px] top-[136px] h-16 w-[3px] rounded-r bg-secondary-700 dark:bg-secondary-600" />

        <div className="relative overflow-hidden rounded-[1.7rem] bg-black">
        {/* NATIVE controls: they give a real SEEK BAR (scrub back/forth), volume and fullscreen for free, and are
            keyboard-accessible — better than a hand-rolled overlay. Our own unmute button stays as the obvious
            affordance for the audio, since the reel autoplays muted. */}
        {failed ? (
          <img
            src={POSTER}
            alt={label}
            className="block h-auto w-full"
            /* If even the poster is gone there is nothing honest to show, so the section removes itself. */
            onError={() => setHidden(true)}
          />
        ) : (
        <video
          ref={videoRef}
          src={blobSrc || SRC}
          poster={POSTER}
          autoPlay={!prefersReducedMotion()}
          muted
          loop
          playsInline
          controls
          controlsList="nodownload"
          preload="metadata"
          onPlay={() => setPlaying(true)}
          onPause={() => setPlaying(false)}
          onVolumeChange={(e) => setMuted((e.currentTarget as HTMLVideoElement).muted)}
          // Only a VIDEO failure may hide the reel. An `error` event bubbling up from a child <track> (a caption
          // file blocked by an extension, a flaky fetch, a bad MIME) must NOT take the video down with it — that
          // is how the reel came to vanish moments after it started playing. Captions are an enhancement; the
          // film is the point. Guard on the event TARGET, and confirm the media element really is in an error
          // state before unmounting anything.
          /**
           * AN ABORTED LOAD IS NOT A BROKEN VIDEO (owner 2026-08-27, staging: the reel disappeared once the
           * page finished settling). The browser aborts a large media request routinely — a re-render, a
           * deprioritised fetch, a cancelled range request — and fires `error` with `MEDIA_ERR_ABORTED`.
           * This handler treated every error as fatal and unmounted the whole card, permanently, for what was
           * usually a transient abort of an 11MB file. Nothing was wrong with the video: it 200s and serves
           * range requests fine.
           *
           * So: an abort is ignored outright, and a REAL failure now degrades to the poster rather than
           * removing the proof from the page — a still from the film is worth more than a gap.
           *
           * A REAL FAILURE IS NOT AUTOMATICALLY THE END, though (2026-09-13). The note above once read
           * "nothing is wrong with the video: it 200s and serves range requests fine" — measured with a tool
           * that asks for no compression. A browser asks for brotli, and our host then answers RANGE requests
           * over the compressed bytes, which is not a film. So before showing a still we try the one thing
           * that does work: fetch the whole file and play it from a blob. Only if that fails too is the
           * poster the honest answer.
           */
          onError={(e) => {
            const v = videoRef.current;
            if (e.target !== v) return;                       // a track/source error, not the video
            if (!v?.error) return;                            // no real media error — keep playing
            if (v.error.code === MEDIA_ERR_ABORTED) return;
            void recover().then((rescued) => { if (!rescued) setFailed(true); });
          }}
          className="block h-auto w-full"
        >
          {/* Captions so the narration is readable with sound off (how most people meet a landing video) and
              for accessibility. `default` shows them immediately; the native player exposes a CC toggle.
              `onError` is swallowed here so a missing/blocked VTT degrades to "no captions", never to no video. */}
          <track
            kind="captions"
            src={CAPTIONS}
            srcLang="en"
            label="English"
            default
            onError={(e) => e.stopPropagation()}
          />
        </video>
        )}
        {/* Kept above the native bar so "there IS sound here" is unmissable while it autoplays muted.
            Hidden once we are showing a still: there is no sound to turn on. */}
        {muted && !failed && (
          <button
            type="button"
            onClick={toggleSound}
            aria-label="Unmute the reel"
            className="absolute right-2 top-2 flex items-center gap-1.5 rounded-full bg-black/60 px-3 py-1.5 text-xs font-medium text-white backdrop-blur transition-colors hover:bg-black/80"
          >
            <VolumeX className="h-3.5 w-3.5" /> Tap for sound
          </button>
        )}
        </div>
      </div>
      <p className="mt-3 text-center text-xs text-secondary-400 dark:text-secondary-500">
        {failed
          ? 'A frame from a short film made entirely in Wisetree AI.'
          : muted ? 'Made entirely in Wisetree AI — tap the speaker for sound.' : 'Made entirely in Wisetree AI.'}
      </p>
    </section>
  );
};

export default ShowcaseReel;
